import { useState } from "react";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useApi, useResource, errorText } from "@/lib/api";

const LABELS = { transactions: "Ledger rows", director_entries: "Director entries", director_adjustments: "Adjustments",
  forecast_items: "Forecast items", financial_years: "Financial years added" };

export default function TestDataTab() {
  const api = useApi();
  const { data, reload } = useResource("/test-data");
  const [busy, setBusy] = useState(false);
  const run = async (method, path, msg) => {
    setBusy(true);
    try { await api(method, path); toast.success(msg); await reload(); } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };

  return (
    <section className="border border-border bg-card p-6 max-w-3xl" data-testid="test-data-section">
      <FlaskConical className="h-5 w-5 text-amber-400" />
      <h3 className="font-display text-xl font-semibold tracking-tight mt-3">Section 7 test data</h3>
      <p className="text-sm text-muted-foreground mt-2">
        Made-up rows built so the totals match the Section 7 figures: FY income, expenditure and net, the 2024-25 July and June
        spot checks, the 2023-24 and 2024-25 category summary, and each director's settlement in both views. Every row is tagged
        <span className="font-mono text-foreground"> [Test]</span> and is removed in one click.
      </p>
      {data && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="test-data-counts">
          {Object.entries(LABELS).map(([k, l]) => (
            <div key={k} className="border border-border p-3">
              <div className="text-[11px] text-muted-foreground">{l}</div>
              <div className="font-mono text-lg" data-testid={`test-data-count-${k}`}>{data.counts[k]}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6 flex gap-3">
        <Button disabled={busy || data?.loaded} onClick={() => run("post", "/test-data/load", "Test data loaded")} data-testid="test-data-load-button">
          Load test data
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={busy || !data?.loaded} data-testid="test-data-remove-button">Remove test data</Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-card">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove all test data?</AlertDialogTitle>
              <AlertDialogDescription>Deletes every row tagged as test data. Your own entries are not touched.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="test-data-remove-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => run("delete", "/test-data", "Test data removed")} data-testid="test-data-remove-confirm">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </section>
  );
}
